// @djsem/shared — Advanced Sync Research (55) + Hardware Safety Boundary (56)

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./index";
import { SeqCounter } from "./runtime";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 55 — Advanced Timecode / Multi-Machine Sync Research
// ============================================================================

/** Where a sync clock event's timing signal originates. `_research` suffixed sources are exploratory only. */
export type DJSEMSyncSourceType =
  | "internal_clock" | "manual_cue" | "beat_link_derived" | "osc_clock"
  | "websocket_clock" | "ableton_link_research" | "mtc_research"
  | "ltc_research" | "external_timecode_research";

/** A sync node's role in a multi-machine sync session. */
export type DJSEMSyncNodeRole = "primary" | "secondary" | "follower" | "observer" | "simulated";
/** A sync node's connectivity/health status. */
export type DJSEMSyncNodeStatus = "online" | "offline" | "unstable" | "desynced" | "unknown";

/** A single timing sample from a sync source, used for drift/jitter research. */
export interface DJSEMSyncClockEvent {
  id: string;
  sessionId: string;
  sourceType: DJSEMSyncSourceType;
  sourceId?: string;
  monotonicTimeMs: number;
  bpm?: number; beat?: number; bar?: number;
  frameRate?: 24 | 25 | 29.97 | 30 | 50 | 60;
  confidence: number;
  driftMs?: number;
  jitterMs?: number;
  warnings: string[];
  createdAt: string;
}

/** A node participating in a multi-machine sync research session. */
export interface DJSEMSyncNode {
  id: string;
  sessionId: string;
  role: DJSEMSyncNodeRole;
  status: DJSEMSyncNodeStatus;
  lastClockEventId?: string;
  estimatedDriftMs?: number;
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** Drift/jitter measurement across a set of clock events for a session. */
export interface DJSEMSyncDriftReport {
  id: string;
  sessionId: string;
  nodeIds: string[];
  sourceType: DJSEMSyncSourceType;
  sampleCount: number;
  averageDriftMs: number;
  maxDriftMs: number;
  jitterMs: number;
  status: "ok" | "warning" | "unstable" | "failed";
  warnings: string[];
  createdAt: string;
}

/** Input for {@link AdvancedSyncResearchService.createClockEvent}. */
export interface CreateSyncClockEventInput {
  sessionId: string; sourceType: DJSEMSyncSourceType;
  monotonicTimeMs: number; confidence: number;
  bpm?: number; beat?: number; bar?: number;
  frameRate?: DJSEMSyncClockEvent["frameRate"];
}

/** Research-only service for recording sync clock events, nodes, and drift across multi-machine sessions. Not a live timecode/hardware output path. */
export class AdvancedSyncResearchService {
  /**
   * Create a sync clock event.
   * @param input - The session, source type, monotonic time, and optional musical position.
   * @returns The new clock event.
   */
  createClockEvent(input: CreateSyncClockEventInput): DJSEMSyncClockEvent {
    return {
      id: `sce_${_seq.next()}`, sessionId: input.sessionId,
      sourceType: input.sourceType, monotonicTimeMs: input.monotonicTimeMs,
      bpm: input.bpm, beat: input.beat, bar: input.bar,
      frameRate: input.frameRate, confidence: input.confidence,
      warnings: [], createdAt: AT,
    };
  }

  /**
   * Validate a clock event's required fields and ranges.
   * @param event - The clock event to validate.
   * @returns The validation result.
   */
  validateClockEvent(event: DJSEMSyncClockEvent): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!event.sessionId) issues.push({ path: "$.sessionId", message: "required" });
    if (event.confidence < 0 || event.confidence > 1) issues.push({ path: "$.confidence", message: "must be 0..1" });
    if (event.monotonicTimeMs < 0) issues.push({ path: "$.monotonicTimeMs", message: "must be >= 0" });
    // Research sources get a warning, not a block
    if (event.sourceType.endsWith("_research")) {
      // valid but noted
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "sync_clock_event");
  }

  /**
   * Register a new sync node for a session.
   * @param sessionId - The sync session id.
   * @param role - The node's role.
   * @returns The new node, initially "online".
   */
  registerNode(sessionId: string, role: DJSEMSyncNodeRole): DJSEMSyncNode {
    return { id: `sn_${_seq.next()}`, sessionId, role, status: "online", warnings: [], createdAt: AT, updatedAt: AT };
  }

  /**
   * Update a node's status.
   * @param node - The node to update.
   * @param status - The new status.
   * @returns The updated node.
   */
  updateNodeStatus(node: DJSEMSyncNode, status: DJSEMSyncNodeStatus): DJSEMSyncNode {
    return { ...node, status, updatedAt: AT };
  }

  /**
   * Compute drift/jitter across a sequence of clock events, assuming a ~1000ms expected interval.
   * @param sessionId - The sync session id.
   * @param events - The clock events to analyze, in chronological order.
   * @param nodeIds - The node ids being compared.
   * @returns The drift report; "unstable" if max drift exceeds 50ms, "warning" if over 20ms.
   */
  calculateDrift(sessionId: string, events: DJSEMSyncClockEvent[], nodeIds: string[]): DJSEMSyncDriftReport {
    if (events.length < 2) return { id: `sdr_${_seq.next()}`, sessionId, nodeIds, sourceType: events[0]?.sourceType ?? "internal_clock", sampleCount: events.length, averageDriftMs: 0, maxDriftMs: 0, jitterMs: 0, status: "ok", warnings: ["Insufficient samples for drift measurement"], createdAt: AT };
    // Simple drift calc: compare monotonic timestamps to expected intervals
    const drifts: number[] = [];
    for (let i = 1; i < events.length; i++) {
      const delta = events[i].monotonicTimeMs - events[i - 1].monotonicTimeMs;
      // Assume ~1000ms expected interval for simulation
      const drift = Math.abs(delta - 1000);
      drifts.push(drift);
    }
    const avg = drifts.reduce((s, d) => s + d, 0) / drifts.length;
    const max = Math.max(...drifts);
    const jitter = Math.sqrt(drifts.reduce((s, d) => s + (d - avg) ** 2, 0) / drifts.length);
    const status = max > 50 ? "unstable" : max > 20 ? "warning" : "ok";
    return { id: `sdr_${_seq.next()}`, sessionId, nodeIds, sourceType: events[0].sourceType, sampleCount: events.length, averageDriftMs: avg, maxDriftMs: max, jitterMs: jitter, status, warnings: status === "unstable" ? ["High drift detected — source may be unreliable"] : [], createdAt: AT };
  }
}

/** Simulates a two-node sync session with synthetic drift, for exercising {@link AdvancedSyncResearchService}. */
export class MultiMachineSyncSimulator {
  /**
   * Simulate a two-node sync session with a sinusoidal drift pattern on the second node.
   * @param sessionId - The sync session id to tag events with.
   * @param tickCount - How many clock events to generate per node.
   * @param driftAmplitudeMs - The amplitude of the simulated drift, in milliseconds.
   * @returns Each node's generated events and the resulting drift report.
   */
  simulateTwoNodes(sessionId: string, tickCount: number, driftAmplitudeMs: number): { events: DJSEMSyncClockEvent[][]; report: DJSEMSyncDriftReport } {
    const svc = new AdvancedSyncResearchService();
    const node1Events: DJSEMSyncClockEvent[] = [];
    const node2Events: DJSEMSyncClockEvent[] = [];
    for (let i = 0; i < tickCount; i++) {
      const t = i * 1000;
      node1Events.push(svc.createClockEvent({ sessionId, sourceType: "internal_clock", monotonicTimeMs: t, confidence: 0.95 }));
      node2Events.push(svc.createClockEvent({ sessionId, sourceType: "internal_clock", monotonicTimeMs: t + Math.sin(i) * driftAmplitudeMs, confidence: 0.9 }));
    }
    const report = svc.calculateDrift(sessionId, node2Events, ["node1", "node2"]);
    return { events: [node1Events, node2Events], report };
  }
}

// ============================================================================
// PROMPT 56 — Hardware Safety Certification Boundary
// ============================================================================

/** Whether a marketing/docs phrase making a hardware-safety claim is allowed, warned, blocked, or needs external certification. */
export type DJSEMSafetyClaimCategory = "allowed" | "warning" | "blocked" | "requires_external_certification";

/** A rule matching a specific forbidden or risky safety-claim phrase. */
export interface DJSEMSafetyClaimRule {
  id: string;
  phrasePattern: string;
  category: DJSEMSafetyClaimCategory;
  reason: string;
  suggestedReplacement?: string;
}

/** A matched blocked/risky safety-claim phrase found by {@link SafetyClaimScannerService.scanText}. */
export interface DJSEMSafetyClaimScanResult {
  filePath: string;
  matchedPhrase: string;
  category: DJSEMSafetyClaimCategory;
  reason: string;
  suggestedReplacement?: string;
  severity: "info" | "warning" | "high" | "critical";
}

/** A hardware/output capability DJSEM may expose. `_future` suffixed capabilities are not real hardware output — simulation/dry-run only. */
export type DJSEMHardwareOutputCapability =
  | "visual_preview" | "browser_simulation" | "osc_dry_run" | "websocket_dry_run"
  | "midi_dry_run" | "dmx_future" | "artnet_future" | "sacn_future"
  | "laser_future" | "timecode_future";

/** How strictly a hardware boundary profile gates its capabilities. */
export type DJSEMHardwareBoundaryMode =
  | "simulation_only" | "dry_run_only" | "operator_review_required"
  | "enterprise_locked" | "disabled";

/** A named set of hardware/output capabilities plus the signoff/entitlement gates required to use them. */
export interface DJSEMHardwareBoundaryProfile {
  id: string;
  label: string;
  capabilities: DJSEMHardwareOutputCapability[];
  mode: DJSEMHardwareBoundaryMode;
  requiresOperatorSignoff: boolean;
  requiresEnterpriseEntitlement: boolean;
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** An operator's recorded acceptance of a hardware boundary profile's warnings. */
export interface DJSEMOperatorSafetySignoff {
  id: string;
  userId: string;
  hardwareBoundaryProfileId: string;
  profileVersionHash: string;
  acceptedWarnings: string[];
  status: "active" | "revoked" | "expired";
  signedAt: string;
  revokedAt?: string;
}

// Blocked phrases — these must never appear in marketing/docs as product claims
const BLOCKED_SAFETY_CLAIMS: DJSEMSafetyClaimRule[] = [
  { id: "bsc_1", phrasePattern: "certified laser safe", category: "blocked", reason: "DJSEM is not certified laser safety software", suggestedReplacement: "simulation-only laser preview" },
  { id: "bsc_2", phrasePattern: "guaranteed safe laser", category: "blocked", reason: "cannot guarantee laser safety", suggestedReplacement: "operator-reviewed laser simulation" },
  { id: "bsc_3", phrasePattern: "automatic venue-safe", category: "blocked", reason: "venue safety requires operator verification", suggestedReplacement: "venue-profile-aware simulation" },
  { id: "bsc_4", phrasePattern: "certified dmx safety", category: "blocked", reason: "DMX safety requires external certification", suggestedReplacement: "DMX dry-run adapter" },
  { id: "bsc_5", phrasePattern: "no operator required", category: "blocked", reason: "operators are always required for hardware", suggestedReplacement: "operator-reviewed workflow" },
  { id: "bsc_6", phrasePattern: "replaces safety operator", category: "blocked", reason: "software cannot replace safety operators", suggestedReplacement: "assists operator workflow" },
  { id: "bsc_7", phrasePattern: "hardware safety certified", category: "blocked", reason: "DJSEM has no hardware safety certification", suggestedReplacement: "software simulation with safety boundaries" },
];

/** Canonical output priority order, lowest index = highest priority (e.g. blackout dominates everything below it). */
export const DJSEM_OUTPUT_PRIORITY_ORDER = [
  "emergency_safe_mode",
  "blackout",
  "operator_manual_stop",
  "operator_manual_override",
  "venue_profile_limit",
  "adapter_safety_limit",
  "show_pack_event",
  "live_conductor_event",
  "semantic_event",
  "energy_fallback",
  "idle_state",
] as const;

/** A single value from {@link DJSEM_OUTPUT_PRIORITY_ORDER}. */
export type DJSEMOutputPriorityLevel = (typeof DJSEM_OUTPUT_PRIORITY_ORDER)[number];

/** Scans text (marketing/docs) for blocked or risky hardware-safety claims. */
export class SafetyClaimScannerService {
  /** @returns A copy of all registered blocked safety-claim rules. */
  getBlockedClaims(): DJSEMSafetyClaimRule[] { return [...BLOCKED_SAFETY_CLAIMS]; }

  /**
   * Scan a text blob for blocked safety-claim phrases.
   * @param text - The text to scan (e.g. a doc or marketing copy).
   * @param filePath - The source file path, used to tag results.
   * @returns Every matched blocked phrase found, with severity and a suggested replacement.
   */
  scanText(text: string, filePath: string): DJSEMSafetyClaimScanResult[] {
    const results: DJSEMSafetyClaimScanResult[] = [];
    const lower = text.toLowerCase();
    for (const rule of BLOCKED_SAFETY_CLAIMS) {
      if (lower.includes(rule.phrasePattern.toLowerCase())) {
        results.push({ filePath, matchedPhrase: rule.phrasePattern, category: rule.category, reason: rule.reason, suggestedReplacement: rule.suggestedReplacement, severity: rule.category === "blocked" ? "critical" : "warning" });
      }
    }
    return results;
  }
}

/** Manages hardware boundary profiles and operator signoffs, and enforces the blackout/safe-mode output priority invariants. */
export class HardwareSafetyBoundaryService {
  /**
   * Create a hardware boundary profile. Future-hardware capabilities automatically
   * require operator signoff and enterprise entitlement.
   * @param label - Human-readable profile label.
   * @param capabilities - The capabilities the profile grants.
   * @param mode - How strictly the profile gates those capabilities.
   * @returns The new profile.
   */
  createProfile(label: string, capabilities: DJSEMHardwareOutputCapability[], mode: DJSEMHardwareBoundaryMode): DJSEMHardwareBoundaryProfile {
    const futureHw = capabilities.some(c => c.includes("future"));
    return {
      id: `hbp_${_seq.next()}`, label, capabilities, mode,
      requiresOperatorSignoff: futureHw || mode === "operator_review_required",
      requiresEnterpriseEntitlement: futureHw || mode === "enterprise_locked",
      warnings: futureHw ? ["Future hardware capabilities require enterprise entitlement and operator signoff."] : [],
      createdAt: AT, updatedAt: AT,
    };
  }

  /**
   * Record an operator's signoff accepting a profile's warnings.
   * @param userId - The operator accepting the profile.
   * @param profile - The profile being signed off on.
   * @param profileVersionHash - Hash identifying the exact profile version signed.
   * @param acceptedWarnings - The warnings the operator explicitly accepted.
   * @returns The new, active signoff.
   */
  createSignoff(userId: string, profile: DJSEMHardwareBoundaryProfile, profileVersionHash: string, acceptedWarnings: string[]): DJSEMOperatorSafetySignoff {
    return { id: `oss_${_seq.next()}`, userId, hardwareBoundaryProfileId: profile.id, profileVersionHash, acceptedWarnings, status: "active", signedAt: AT };
  }

  /**
   * Revoke an operator signoff.
   * @param signoff - The signoff to revoke.
   * @returns The updated, revoked signoff.
   */
  revokeSignoff(signoff: DJSEMOperatorSafetySignoff): DJSEMOperatorSafetySignoff {
    return { ...signoff, status: "revoked", revokedAt: AT };
  }

  /**
   * Check whether a profile's capabilities may currently be enabled.
   * @param profile - The profile to check.
   * @param signoff - The operator's current signoff, if any.
   * @returns Valid only if signoff/entitlement requirements are met and the profile isn't disabled.
   */
  canEnableCapability(profile: DJSEMHardwareBoundaryProfile, signoff?: DJSEMOperatorSafetySignoff): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (profile.requiresOperatorSignoff && (!signoff || signoff.status !== "active")) {
      issues.push({ path: "$.signoff", message: "Active operator signoff required" });
    }
    if (profile.requiresEnterpriseEntitlement) {
      issues.push({ path: "$.entitlement", message: "Enterprise entitlement required" });
    }
    if (profile.mode === "disabled") {
      issues.push({ path: "$.mode", message: "Profile is disabled" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "hardware_capability");
  }

  /**
   * Verify blackout dominates all lower-priority events.
   * @param eventPriority - The event priority level to check.
   * @returns Valid if the event cannot override blackout (i.e. is blackout/emergency_safe_mode or ranks below it).
   */
  assertBlackoutDominates(eventPriority: DJSEMOutputPriorityLevel): DJSEMValidationResult {
    const blackoutIdx = DJSEM_OUTPUT_PRIORITY_ORDER.indexOf("blackout");
    const eventIdx = DJSEM_OUTPUT_PRIORITY_ORDER.indexOf(eventPriority);
    const ok = eventIdx >= blackoutIdx; // blackout has lower index = higher priority
    return toDJSEMValidationResult({ valid: ok || eventPriority === "blackout" || eventPriority === "emergency_safe_mode", issues: ok ? [] : [{ path: "$.priority", message: `${eventPriority} cannot override blackout` }] }, "blackout_priority");
  }

  /**
   * Verify emergency safe mode dominates all lower-priority events.
   * @param eventPriority - The event priority level to check.
   * @returns Valid if the event cannot override emergency_safe_mode.
   */
  assertSafeModeDominates(eventPriority: DJSEMOutputPriorityLevel): DJSEMValidationResult {
    const safeModeIdx = DJSEM_OUTPUT_PRIORITY_ORDER.indexOf("emergency_safe_mode");
    const eventIdx = DJSEM_OUTPUT_PRIORITY_ORDER.indexOf(eventPriority);
    const ok = eventIdx >= safeModeIdx;
    return toDJSEMValidationResult({ valid: ok || eventPriority === "emergency_safe_mode", issues: ok ? [] : [{ path: "$.priority", message: `${eventPriority} cannot override emergency_safe_mode` }] }, "safe_mode_priority");
  }
}

/** Non-certification statement — must be included in all venue/hardware docs */
export const DJSEM_NON_CERTIFICATION_STATEMENT = "DJSEM is not certified hardware safety software. DJSEM simulation, dry-run, event output, venue profiles, and adapter mappings do not replace trained operators, physical emergency stops, venue safety procedures, legal requirements, or external safety certification.";
